import './controls-all.ts';
import { createStytchB2BClient, StytchB2B } from '@stytch/vanilla-js/b2b';
import { bindChartTooltips } from './charts.ts';
import { api, resetListPages, setSessionSource } from './console/api.ts';
import {
  type CarrierView,
  type PlanNames,
  setCarrierNames,
  setCarrierOffers,
  setPlanNames,
} from './console/carriers.ts';
import { bindIdCopy } from './console/components.ts';
import { ApiError, explain } from './console/errors.ts';
import { esc, icon, ROLE, setAgentNames } from './console/format.ts';
import { wireMenuSheet } from './console/menu-sheet.ts';
import { viewOn } from './console/modules.ts';
import { navHtml, paintNav, quickHtml, wireNav } from './console/nav.ts';
import { forgetClosedNotice, hideNotice, paintNotice } from './console/notice.ts';
import { forgetRetryKeys } from './console/retry-keys.ts';
import {
  changeAction,
  clickAction,
  focusAction,
  inputAction,
  onRendered,
  registerActions,
  registerViews,
  render,
  setCurrent,
  submitAction,
} from './console/router.ts';
import {
  has,
  isNubiOrg,
  type Me,
  me,
  seesPortfolio,
  setMe,
  userBlock,
  workplace,
} from './console/session.ts';
import { wireSidebar } from './console/sidebar.ts';
import { $, toast } from './console/ui.ts';
import { bindControls } from './controls-define.ts';
import { bindFilePickers } from './file-picker.ts';
import { loginFonts, simulatorLogin, stytchLoginConfig } from './login.ts';
import { startQaMode } from './qa-boot.ts';
import type { FeedbackSetting } from './qa-mode.ts';
import { actions as sharedActions } from './shared-actions.ts';
import { SPANISH } from './stytch-es.ts';
import { stytchTheme } from './theme.ts';
import {
  administradores,
  actions as administradoresActions,
  resetAdministradores,
} from './views/administradores.ts';
import { agencias, actions as agenciasActions, resetAgencias } from './views/agencias.ts';
import { antecedentes, actions as antecedentesActions } from './views/antecedentes.ts';
import {
  aseguradoras,
  actions as aseguradorasActions,
  resetRemittances,
} from './views/aseguradoras.ts';
import {
  aseguradorasNubi,
  actions as aseguradorasNubiActions,
  resetAseguradorasNubi,
} from './views/aseguradoras-nubi.ts';
import {
  avisoGlobal,
  actions as avisoGlobalActions,
  resetAvisoGlobal,
} from './views/aviso-global.ts';
import { avisos, actions as avisosActions, refreshBadge } from './views/avisos.ts';
import { bitacora, actions as bitacoraActions, resetBitacora } from './views/bitacora.ts';
import { boleta, actions as boletaActions, resetBoleta } from './views/boleta.ts';
import { caja, actions as cajaActions } from './views/caja.ts';
import { cartera } from './views/cartera.ts';
import {
  clientes,
  actions as clientesActions,
  closeParty,
  resetParties,
} from './views/clientes.ts';
import { comisiones, actions as comisionesActions, resetCommissions } from './views/comisiones.ts';
import {
  conocimiento,
  actions as conocimientoActions,
  resetKnowledge,
} from './views/conocimiento.ts';
import { cotizar, actions as cotizarActions, resetQuoting } from './views/cotizar.ts';
import { cursos, actions as cursosActions, resetCourses } from './views/cursos.ts';
import { equipo, actions as equipoActions, resetEquipo } from './views/equipo.ts';
import { estadisticas, actions as estadisticasActions, resetStats } from './views/estadisticas.ts';
import { expediente, actions as expedienteActions } from './views/expediente.ts';
import {
  actions as integracionActions,
  integracionGyt,
  resetIntegration,
} from './views/integracion.ts';
import {
  liquidaciones,
  actions as liquidacionesActions,
  resetSettlements,
} from './views/liquidaciones.ts';
import { metas, actions as metasActions, resetGoals } from './views/metas.ts';
import { misTareas, actions as misTareasActions, resetMyTasks } from './views/mis-tareas.ts';
import {
  modulosAgencia,
  actions as modulosAgenciaActions,
  resetModulosAgencia,
} from './views/modulos-agencia.ts';
import { onboarding, actions as onboardingActions, resetOnboarding } from './views/onboarding.ts';
import { ordenes, actions as ordenesActions, resetOrders } from './views/ordenes.ts';
import { actions as polizaAccionesActions } from './views/poliza-acciones.ts';
import { bindPolicyTabs } from './views/poliza-detalle.ts';
import {
  bindMoreMenus,
  polizas,
  actions as polizasActions,
  resetPolicies,
  restorePoliciesUrl,
  selectPolicyTab,
} from './views/polizas.ts';
import { productos, actions as productosActions, resetRules } from './views/productos.ts';
import { prospectos, actions as prospectosActions, resetLeads } from './views/prospectos.ts';
import {
  recordatorios,
  actions as recordatoriosActions,
  resetReminders,
} from './views/recordatorios.ts';
import {
  renovaciones,
  actions as renovacionesActions,
  resetRenewals,
} from './views/renovaciones.ts';
import { resumen } from './views/resumen.ts';
import { resetFollowUp, actions as seguimientoActions } from './views/seguimiento.ts';
import { resetClaims, siniestros, actions as siniestrosActions } from './views/siniestros.ts';

/**
 * Nubi's agency console (MVP). Login is Stytch B2B's prebuilt UI (discovery: Google, email +
 * password, email code or magic link; src/login.ts); every API call carries the Stytch session JWT, and the API decides the
 * agency and permissions from it. Design rules: .claude/skills/nubi-frontend.
 *
 * This file only starts the console: login, theme, session and menu. The shared infrastructure
 * lives in console/ (api, session, format, errors, components, ui, router, nav); each view lives
 * in views/ with its table of actions, and the buttons every screen shares in shared-actions.ts
 * (features/console/consola-por-modulos.feature). Every event of #app goes to those tables.
 */

const config = (await (await fetch('/config.json')).json()) as (
  | { identity: 'stytch'; stytchPublicToken: string }
  | { identity: 'simulator'; loginUrl: string }
) & { feedback?: FeedbackSetting; agentApplication?: boolean };
// ------------------------------------------------------------------ «Modo QA» (dev/QA only)

// The API decides whether it exists (never in production). The factory's feedback widget is
// loaded apart, from this origin, only when the person turns the QA mode on: the console itself
// never ships it. The boot is the one every page shares (qa-boot.ts); the console adds the button.
const qa = await startQaMode({ config, explain: (message) => void toast(message, 'error') });
// The same button in the sidebar and, discreet in a corner, on the login screen.
const qaButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-qa-mode]')];
const paintQaButtons = () => {
  $('#qa-row').hidden = !qa.visible;
  for (const b of qaButtons) {
    if (!b.closest('#qa-row')) b.hidden = !qa.visible;
    b.setAttribute('aria-pressed', String(qa.active));
    b.setAttribute('aria-label', qa.label);
    b.title = qa.label;
    b.innerHTML = `${icon('target')}<span class="label">${esc(qa.label)}</span>`;
  }
};
paintQaButtons();
for (const b of qaButtons)
  b.addEventListener('click', () => {
    qa.press();
    paintQaButtons();
  });
/** Local and ephemeral environments without Stytch log in through the identity simulator. */
const simulator = config.identity === 'simulator' ? config : null;
const stytch =
  config.identity === 'stytch' ? createStytchB2BClient(config.stytchPublicToken) : null;
const SIM_KEY = 'nubi.simulator.session_jwt';
if (simulator) {
  const url = new URL(location.href);
  const jwt = url.searchParams.get('session_jwt');
  if (jwt) {
    sessionStorage.setItem(SIM_KEY, jwt);
    history.replaceState(null, '', '/');
  }
}
const sessionJwt = () =>
  simulator ? sessionStorage.getItem(SIM_KEY) : stytch?.session.getTokens()?.session_jwt;
// Every request of every view carries this session's JWT.
setSessionSource(sessionJwt);

// ------------------------------------------------------------------ navigation & actions

registerViews({
  resumen,
  estadisticas,
  cotizar,
  expediente,
  productos,
  prospectos,
  clientes,
  conocimiento,
  polizas,
  renovaciones,
  siniestros,
  caja,
  cartera,
  aseguradoras,
  ordenes,
  onboarding,
  cursos,
  antecedentes,
  boleta,
  avisos,
  comisiones,
  metas,
  liquidaciones,
  recordatorios,
  'integracion-gyt': integracionGyt,
  // «Administración de Nubi» (ADR-0023): only Nubi's own organization sees it.
  agencias,
  'modulos-agencia': modulosAgencia,
  'aseguradoras-nubi': aseguradorasNubi,
  'aviso-global': avisoGlobal,
  administradores,
  bitacora,
  // The agency's team (#313).
  equipo,
  // Each person's tasks (#317).
  'mis-tareas': misTareas,
});
// Each view answers its own buttons, forms and controls. The buttons every screen shares come
// last: a view's own action wins (an aviso's «Ir» marks it read on the way).
registerActions(
  cotizarActions,
  polizasActions,
  renovacionesActions,
  siniestrosActions,
  polizaAccionesActions,
  prospectosActions,
  clientesActions,
  conocimientoActions,
  estadisticasActions,
  cajaActions,
  aseguradorasActions,
  liquidacionesActions,
  comisionesActions,
  metasActions,
  ordenesActions,
  expedienteActions,
  productosActions,
  avisosActions,
  recordatoriosActions,
  onboardingActions,
  cursosActions,
  antecedentesActions,
  boletaActions,
  integracionActions,
  agenciasActions,
  modulosAgenciaActions,
  aseguradorasNubiActions,
  avisoGlobalActions,
  administradoresActions,
  bitacoraActions,
  equipoActions,
  misTareasActions,
  seguimientoActions,
  sharedActions,
);
onRendered(() => void refreshBadge());

$('#app').addEventListener('submit', submitAction);
bindChartTooltips($('#app'));
$('#app').addEventListener('click', (ev) => {
  const t = (ev.target as HTMLElement).closest<HTMLElement>('button');
  if (t) clickAction(t);
});

// The file pickers (rate sheet, invoice, CSV, materials) show the chosen file and «Quitar».
bindFilePickers(document);
bindControls(document);
// A short id on screen copies whole (#235).
bindIdCopy(document);
// «Más acciones» of a policy closes on a pick, a click elsewhere or Escape.
bindMoreMenus(document);
// The tabs of a policy's page move with the arrows, Home and End (#310).
bindPolicyTabs(document, selectPolicyTab);
// «Atrás» and «Adelante» of the browser between the policies' list and a policy's page (#310).
addEventListener('popstate', () => {
  if (me && restorePoliciesUrl(location.search)) void render('polizas', { quiet: true });
});

$('#app').addEventListener('change', changeAction);
$('#app').addEventListener('input', inputAction);
$('#app').addEventListener('focusin', focusAction);

// The menu is built from its model (console/nav.ts): groups that open and close, and the arrows.
$('#nav').innerHTML = navHtml();
// The sidebar folds into a rail of icons (#188), as the person left it in this browser; a
// group's button in the rail opens the sidebar with that group open.
wireNav(
  $('#nav'),
  wireSidebar({
    shell: $('#shell'),
    toggle: $<HTMLButtonElement>('#side-toggle'),
    nav: $('#nav'),
    tip: $('#side-tip'),
    storage: () => {
      try {
        return localStorage;
      } catch {
        return null;
      }
    },
    wide: () => matchMedia('(min-width: 881px)').matches,
  }),
);
// On a phone the menu is a sheet that «Menú» opens (#237); the top bar keeps Avisos and the
// role's main destination (#nav-quick).
const phone = matchMedia('(max-width: 880px)');
const menuSheet = wireMenuSheet({
  opener: $<HTMLButtonElement>('#menu-open'),
  sheet: $('#menu-sheet'),
  scrim: $('#menu-scrim'),
  closer: $<HTMLButtonElement>('#menu-close'),
  outside: [$('#main'), $('#nav-quick'), $('.side-head')],
  phone: () => phone.matches,
});
const openFromMenu = (ev: Event) => {
  const view = (ev.target as HTMLElement).closest<HTMLElement>('button')?.dataset.view;
  if (!view) return;
  // The sheet closes first: focus goes to the screen, not back to «Menú».
  menuSheet.close(false);
  // «Clientes» in the menu is the list, even when a customer's page was open.
  if (view === 'clientes') closeParty();
  // «Pólizas y recibos» in the menu is the list, without the policy that was open.
  if (view === 'polizas') resetPolicies();
  // A view opened from the menu starts again at its first page.
  resetListPages();
  void render(view);
  $('#main').focus({ preventScroll: true });
  window.scrollTo({ top: 0 });
};
$('#nav').addEventListener('click', openFromMenu);
$('#nav-quick').addEventListener('click', openFromMenu);
$('#logout').addEventListener('click', () => {
  if (simulator) {
    sessionStorage.removeItem(SIM_KEY);
    showLogin();
  } else void stytch?.session.revoke();
});

// ------------------------------------------------------------------ theme

const dark = () =>
  document.documentElement.dataset.theme
    ? document.documentElement.dataset.theme === 'dark'
    : matchMedia('(prefers-color-scheme: dark)').matches;
const paintThemeButton = () => {
  $('#theme').innerHTML =
    `${icon(dark() ? 'sun' : 'moon')}<span class="label">${dark() ? 'Claro' : 'Oscuro'}</span>`;
  $('#theme').setAttribute('aria-label', dark() ? 'Usar tema claro' : 'Usar tema oscuro');
};
$('#theme').addEventListener('click', () => {
  const next = dark() ? 'light' : 'dark';
  document.documentElement.dataset.theme = next;
  try {
    localStorage.setItem('nubi.theme', next);
  } catch {
    // Private windows may refuse storage; the theme still applies to this visit.
  }
  paintThemeButton();
});
matchMedia('(prefers-color-scheme: dark)').addEventListener('change', paintThemeButton);

// ------------------------------------------------------------------ session

/**
 * Forgets everything of the person who used the console before (a shared counter PC): their
 * quote, customer, prospect, drafts, open files and choices. Called when anyone signs in and
 * when anyone signs out, so nothing survives from one session to the next.
 */
function resetSession() {
  setMe(null);
  // The platform's notice was the previous person's (ADR-0028).
  hideNotice($('#platform-notice'));
  setCarrierNames({});
  setAgentNames({});
  setPlanNames({});
  // The quote on screen, its catalog, drafts, open file, customer and prospect.
  resetQuoting();
  resetLeads();
  resetRenewals();
  resetClaims();
  resetMyTasks();
  resetFollowUp();
  resetParties();
  resetPolicies();
  resetKnowledge();
  resetStats();
  // Remittance and statement drafts, refused invoices, commission filters, goals and their plan.
  resetRemittances();
  resetSettlements();
  resetCommissions();
  resetGoals();
  // The orders on screen, the rule proposal left open and the reminders' texts.
  resetOrders();
  resetRules();
  resetReminders();
  // The dossiers' boletas left open, the course being edited and the form editor's version.
  resetOnboarding();
  resetCourses();
  resetBoleta();
  resetIntegration();
  resetAgencias();
  resetModulosAgencia();
  resetAseguradorasNubi();
  resetAvisoGlobal();
  resetAdministradores();
  resetBitacora();
  resetEquipo();
  setCurrent('resumen');
}

/** Where each person starts: their portfolio, or the one screen their role works on. */
function landing() {
  // Nubi's own organization opens its administration, never an agency's screen (ADR-0023).
  if (isNubiOrg()) return 'agencias';
  if (seesPortfolio()) return 'resumen';
  // Their one screen, unless its module is off for the agency (ADR-0029).
  if (has('evaluator') && viewOn('antecedentes')) return 'antecedentes';
  if (has('training_manager') && viewOn('cursos')) return 'cursos';
  return 'avisos';
}

async function showApp() {
  resetSession();
  let me: Me;
  try {
    me = await api<Me>('GET', '/v1/me');
    setMe(me);
  } catch (e) {
    showLogin();
    // An agency that cannot enter (suspended, not registered) is told why, in the login card
    // itself, until someone enters (ADR-0024): a toast would go away.
    if (e instanceof ApiError && e.code.startsWith('AGENCY_')) {
      const notice = $('#login-notice');
      const text = notice.querySelector('span');
      if (text) text.textContent = explain(e).human;
      notice.hidden = false;
      notice.focus();
      return;
    }
    toast('No se pudo abrir tu sesión en Nubi.', 'error', explain(e).detail);
    return;
  }
  $('#login-notice').hidden = true;
  $('#login').hidden = true;
  $('#shell').hidden = false;
  const role = me.roles.map((r) => ROLE[r] ?? r).join(' · ');
  // The person by their name (#260), their e-mail as the hint; by their e-mail without a name.
  const user = userBlock(me);
  $('#who').textContent = user.who;
  if (user.hint) $('#who').title = user.hint;
  else $('#who').removeAttribute('title');
  // «Dirección · Agencia Morales»; Nubi's organization has no agency (ADR-0023).
  $('#who-role').textContent = isNubiOrg() ? role : `${role} · ${workplace()}`;
  $('#avatar').textContent = user.monogram;
  // The platform's notice to every agency (ADR-0028), unless this person closed it in this tab.
  paintNotice($('#platform-notice'), me);
  // Each person sees their part of the menu (the API and Cerbos decide what they may do).
  paintNav($('#nav'));
  $('#nav-quick').innerHTML = quickHtml();
  paintThemeButton();
  // Carriers are named as their packs name them (the agency's own list; Nubi's has none).
  // With their status in the platform (#283): «En pruebas», and a saved quote of one turned off.
  if (!isNubiOrg()) {
    const [cs, plans] = await Promise.all([
      api<CarrierView[]>('GET', '/v1/carriers').catch(() => [] as CarrierView[]),
      // The carriers' plans as their catalogs name them (#307): «Cotizar» and the files use them.
      api<PlanNames>('GET', '/v1/catalog/plan-names').catch(() => ({}) as PlanNames),
    ]);
    setPlanNames(plans);
    setCarrierNames(Object.fromEntries(cs.map((c) => [c.id, c.name])));
    // Agents by their name, not their code (#313); by their code if the directory cannot be read.
    const agents = await api<{ agentId: string; name: string }[]>(
      'GET',
      '/v1/team/directory',
    ).catch(() => [] as { agentId: string; name: string }[]);
    setAgentNames(Object.fromEntries(agents.map((a) => [a.agentId, a.name])));
    setCarrierOffers(
      Object.fromEntries(
        cs.map((c) => [
          c.id,
          { platformStatus: c.platformStatus ?? 'ACTIVE', offered: c.offered ?? true },
        ]),
      ),
    );
  }
  // A link to «Pólizas y recibos» (a policy's page, or the list with its filters) opens there.
  const linked = seesPortfolio() && viewOn('polizas') && restorePoliciesUrl(location.search);
  await render(linked ? 'polizas' : landing());
}

/** Concentric, slightly irregular contour lines: a quiet topographic backdrop for the login. */
function contours() {
  const svg = $('#contours');
  const { width: w, height: h } = svg.getBoundingClientRect();
  if (!w || !h) return;
  const cx = w * 0.78;
  const cy = h * 0.92;
  let paths = '';
  for (let k = 1; k <= 22; k++) {
    const r = 26 * k;
    let d = '';
    for (let i = 0; i <= 96; i++) {
      const a = (i / 96) * Math.PI * 2;
      const wobble = 1 + 0.07 * Math.sin(3 * a + k * 0.5) + 0.04 * Math.sin(5 * a - k * 0.3);
      d += `${i ? 'L' : 'M'}${(cx + Math.cos(a) * r * 1.35 * wobble).toFixed(1)} ${(cy + Math.sin(a) * r * wobble).toFixed(1)}`;
    }
    paths += `<path d="${d}Z" fill="none" stroke="currentColor" stroke-width="${k % 5 === 0 ? 1.4 : 0.8}"/>`;
  }
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.innerHTML = paths;
}

function showLogin() {
  // Nothing of the previous person stays on a shared counter PC: not their quote, not their
  // customer or prospect, not their drafts, not the screen they left open.
  resetSession();
  // Nor the requests they left in course with a carrier (#219). Only here, when the session
  // ends: showApp also resets on a page reload, and a reload must keep following them.
  forgetRetryKeys();
  // And the notice they closed: the next person sees it (ADR-0028).
  forgetClosedNotice();
  $('#app').innerHTML = '';
  menuSheet.close(false);
  $('#shell').hidden = true;
  $('#login').hidden = false;
  // The home page invites to apply as an agent only while the public boleta is on (in the brand
  // panel on the desktop, after the card on a phone: styles.css shows one of the two).
  for (const join of document.querySelectorAll<HTMLElement>('[data-join]'))
    join.hidden = !config.agentApplication;
  contours();
  if (simulator) {
    $('#stytch-login').innerHTML = simulatorLogin(simulator.loginUrl, location.origin);
    return;
  }
  if (!stytch) return;
  // The StytchB2B web component (Stytch's recommended way; its deprecated mount() helper
  // creates the element without applying client/config in SDK 6.3.0).
  if (!customElements.get('stytch-b2b')) customElements.define('stytch-b2b', StytchB2B);
  const container = $('#stytch-login');
  if (!container.querySelector('stytch-b2b')) {
    const login = document.createElement('stytch-b2b') as HTMLElement & Record<string, unknown>;
    login.client = stytch;
    login.strings = SPANISH;
    const forced = document.documentElement.dataset.theme;
    // Stytch lives in its own shadow DOM: the tokens go to it by hand (theme.ts).
    login.presentation = {
      theme:
        forced === 'dark'
          ? stytchTheme.dark
          : forced === 'light'
            ? stytchTheme.light
            : [stytchTheme.light, stytchTheme.dark],
      options: { hideHeaderText: true },
    };
    // Google, email + password, and a code or link by email (src/login.ts).
    login.config = stytchLoginConfig(location.origin);
    container.append(login);
  }
}
addEventListener('resize', () => {
  if (!$('#login').hidden) contours();
});

stytch?.session.onChange((session) => {
  if (session && !me) void showApp();
  if (!session && me) showLogin();
});
if (simulator ? sessionJwt() : stytch?.session.getSync()) await showApp();
else {
  // The login's fonts before it shows, so its text does not move when they arrive (#240).
  await loginFonts(document.fonts);
  showLogin();
}
